import { useState, useCallback, useRef } from "react";

interface TooltipState {
  content: string;
  position: { x: number; y: number };
  visible: boolean;
}

const useTooltip = (): [
  TooltipState,
  (content: string, event: React.MouseEvent) => void,
  () => void
] => {
  const [tooltipContent, setTooltipContent] = useState<string>("");
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastContentRef = useRef<string>("");

  const handleMouseEnter = useCallback((content: string, event: React.MouseEvent) => {
    if (!content || content === lastContentRef.current) return;
    
    // Annule le timeout précédent s'il existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Ajoute un petit délai pour éviter les clignotements
    timeoutRef.current = setTimeout(() => {
      lastContentRef.current = content;
      setTooltipContent(content);
      setTooltipVisible(true);

      // Trouve le conteneur parent avec overflow-y-auto
      const scrollContainer = (event.target as HTMLElement).closest(
        ".overflow-y-auto"
      );
      
      const targetElement = event.target as HTMLElement;
      const targetRect = targetElement.getBoundingClientRect();
      
      // Position de base du tooltip (par rapport à l'élément survolé)
      let x = targetRect.left + (targetRect.width / 2); // Centre horizontalement
      let y = targetRect.top - 10; // 10px au-dessus de l'élément
      
      // Ajuste la position en fonction de la taille du tooltip estimée
      const tooltipWidth = 400; // Largeur max du tooltip
      const tooltipHeight = 100; // Hauteur estimée du tooltip
      
      // Vérifie si le tooltip sort de la fenêtre à droite
      if (x + (tooltipWidth / 2) > window.innerWidth) {
        x = targetRect.right - (tooltipWidth / 2);
      }
      
      // Vérifie si le tooltip sort de la fenêtre à gauche
      if (x - (tooltipWidth / 2) < 0) {
        x = targetRect.left + (tooltipWidth / 2);
      }
      
      // Vérifie si le tooltip sort de la fenêtre en haut
      if (y - tooltipHeight < 0) {
        y = targetRect.bottom + 10; // Place le tooltip en dessous de l'élément
      }
      
      // Si on est dans un conteneur avec scroll, ajuste la position
      if (scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        
        // Vérifie si le tooltip sort du conteneur en bas
        if (y + tooltipHeight > containerRect.bottom) {
          y = targetRect.top - tooltipHeight - 10;
        }
        
        // Vérifie si le tooltip sort du conteneur en haut
        if (y < containerRect.top) {
          y = targetRect.bottom + 10;
        }
      }

      setTooltipPosition({ x, y });
    }, 100); // Délai de 100ms
  }, []);

  const handleMouseLeave = useCallback(() => {
    // Annule le timeout s'il existe
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    setTooltipVisible(false);
    setTooltipContent("");
    setTooltipPosition({ x: 0, y: 0 });
    lastContentRef.current = "";
  }, []);

  return [
    { content: tooltipContent, position: tooltipPosition, visible: tooltipVisible },
    handleMouseEnter,
    handleMouseLeave,
  ];
};

export default useTooltip;
